/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef, type JSX } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MdmDelivery.module.scss';

type DeliveryItem = {
  name: string;
  desc: string;
  icon: JSX.Element;
  ctas: { label: string; href: string }[];
};

type DeliveryGroup = {
  category: string;
  items: DeliveryItem[];
};

const ApiIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />
  </svg>
);

const WarehouseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <path d="M12 15V3" />
  </svg>
);

const CrmIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx={12} cy={6} rx={8} ry={3} />
    <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
    <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
  </svg>
);

const McpIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3H7a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h1" />
    <path d="M16 3h1a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-1" />
  </svg>
);

const SparkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3.5 13.7 9l5.5 1.7L13.7 12.4 12 18l-1.7-5.6L4.8 10.7 10.3 9 12 3.5Z" />
  </svg>
);

const RegisIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const ArrowIcon = () => (
  <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getGroups = (t: (key: string) => string): DeliveryGroup[] => [
  {
    category: t('Integrate'),
    items: [
      {
        name: t('Company Data API'),
        icon: <ApiIcon />,
        desc: t(
          'Resolve a record on write. Send a name and country, receive the entity, identifier, confidence, and matched attributes.',
        ),
        ctas: [{ label: t('Read the docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
      },
      {
        name: t('Batch & warehouse'),
        icon: <WarehouseIcon />,
        desc: t(
          'Match an existing file at volume, or land resolved records and change events straight into your warehouse on a schedule.',
        ),
        ctas: [{ label: t('Talk to us'), href: '/contact-us' }],
      },
    ],
  },
  {
    category: t('Connect'),
    items: [
      {
        name: 'CRM',
        icon: <CrmIcon />,
        desc: t(
          'Dedupe accounts, fix parent-child structures, and enrich on create — inside Salesforce, HubSpot, or Dynamics 365, with the source written alongside every field.',
        ),
        ctas: [
          { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
          { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
        ],
      },
      {
        name: 'MCP',
        icon: <McpIcon />,
        desc: t(
          'Connect Global Database to any MCP-compatible client or agent through the Model Context Protocol — company data queried in natural language, with sources attached.',
        ),
        ctas: [{ label: t('MCP docs'), href: '/mcp-integration' }],
      },
    ],
  },
  {
    category: t('Work with it'),
    items: [
      {
        name: 'Claude & ChatGPT',
        icon: <SparkIcon />,
        desc: t(
          'Ask master-data questions in plain language — duplicates by group, unresolved records, what changed this week — with sources attached.',
        ),
        ctas: [
          { label: 'Claude', href: 'https://claude.ai/new#directory/connectors/global-database' },
          { label: 'ChatGPT', href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
        ],
      },
      {
        name: 'Regis AI',
        icon: <RegisIcon />,
        desc: t(
          "Global Database's built-in analyst. Ask for duplicate groups, unresolved records, or what changed this week — answered in-platform, traceable to the filing.",
        ),
        ctas: [
          { label: t('Meet Regis'), href: '/book-a-demo' },
          { label: t('Data dictionary'), href: '/data-dictionary' },
        ],
      },
    ],
  },
];

export const MdmDelivery = () => {
  const { t } = useInt();
  const gridRef = useRef<HTMLDivElement>(null);
  const hasIntersected = useIntersection(gridRef, { threshold: 0.08 });

  const groups = getGroups(t);

  let flatIndex = 0;

  return (
    <Section className={styles.root}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={cn(styles.head, hasIntersected && styles.headIn)}>
        <TitleBlock
          title={t('Into the systems that hold the record')}
          description={t(
            'Match and enrichment run where your master data already lives — through the API, as scheduled batches, in the warehouse, or directly inside the CRM.',
          )}
          align="left"
        />
      </div>

      <div ref={gridRef} className={styles.grid}>
        {groups.map((group, gi) => (
          <div key={group.category} className={cn(styles.group, hasIntersected && styles.groupIn)}>
            <div className={styles.category} style={{ '--gi': gi } as CSSProperties}>
              {group.category}
              <span className={styles.categoryBar} />
            </div>
            {group.items.map((item) => {
              const i = flatIndex;
              flatIndex += 1;

              return (
                <div
                  key={item.name}
                  className={cn(styles.item, hasIntersected && styles.itemIn)}
                  style={{ '--i': i } as CSSProperties}
                >
                  <div className={styles.itemTop}>
                    <span className={styles.icon}>{item.icon}</span>
                    <span className={styles.name}>{item.name}</span>
                  </div>
                  <p className={styles.itemDesc}>{item.desc}</p>
                  <div className={styles.ctas}>
                    {item.ctas.map((cta) => (
                      <a key={cta.label} className={styles.cta} href={cta.href}>
                        {cta.label}
                        <ArrowIcon />
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Section>
  );
};
